/**
 * Inline images inside an entry's body — the web half of
 * longreads-app/src/utils/entryContent.ts.
 *
 * Entry content is a single string with images embedded as tokens:
 *
 *   [[img:https://…]]           a plain inline image
 *   [[img:https://…|scratch]]   hidden behind a scratch-card in the app
 *
 * The token format has to stay byte-identical to the app's, or an entry
 * written on a phone renders raw `[[img:…]]` text on the web.
 */

const IMAGE_TOKEN = /\[\[img:([^\]]+?)(\|scratch)?\]\]/g;

/** Splits body text into ordered text/image blocks for the reader. */
export const parseEntryContent = (content) => {
  if (!content) return [];

  const blocks = [];
  let cursor = 0;

  // matchAll needs the regex reset — it is module-level and stateful with /g.
  IMAGE_TOKEN.lastIndex = 0;
  for (const match of content.matchAll(IMAGE_TOKEN)) {
    const start = match.index ?? 0;
    const before = content.slice(cursor, start).trim();
    if (before) blocks.push({ kind: 'text', text: before });
    blocks.push({ kind: 'image', url: match[1], scratch: !!match[2] });
    cursor = start + match[0].length;
  }

  const rest = content.slice(cursor).trim();
  if (rest) blocks.push({ kind: 'text', text: rest });

  return blocks;
};

/** Body text with image tokens removed — for previews and meta descriptions. */
export const stripImageTokens = (content) =>
  (content || '')
    .replace(IMAGE_TOKEN, ' ')
    .replace(/\s+/g, ' ')
    .trim();
